/**
 * LiveVideoGrid — renders the WebRTC mesh peers as a responsive grid of tiles.
 * Local user shows their own preview from `localStream` (or screen if active).
 * Designed to be embedded inside LiveClassroomRoom.
 */
import React, { useEffect, useRef } from 'react';
import { Mic, MicOff, Video as VideoIcon, VideoOff, Monitor, User } from 'lucide-react';

function VideoTile({ stream, name, role, micOn, camOn, screenOn, isLocal, big, isHost, isPresenter }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) {
      ref.current.srcObject = stream;
    }
  }, [stream]);

  const showVideo = stream && (camOn || screenOn || isLocal);

  return (
    <div
      data-testid={`video-tile-${isLocal ? 'local' : (name || 'peer').toLowerCase().replace(/\s+/g, '-')}`}
      data-host={isHost ? 'true' : undefined}
      data-presenter={isPresenter ? 'true' : undefined}
      data-spotlight={big ? 'true' : undefined}
      className={[
        "relative bg-slate-800 rounded-lg overflow-hidden border transition-all",
        big ? "col-span-3 row-span-2 ring-2 ring-amber-400 shadow-2xl shadow-amber-500/20 border-amber-300/40" : "border-slate-700",
        !big && isHost ? "ring-1 ring-indigo-400/60" : "",
      ].join(' ')}
    >
      {showVideo ? (
        <video
          ref={ref}
          autoPlay
          playsInline
          muted={isLocal /* never echo own mic */}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full grid place-items-center text-slate-400 aspect-video">
          <div className="text-center">
            <div className={`rounded-full bg-slate-700 grid place-items-center mx-auto mb-1 ${big ? 'h-20 w-20' : 'h-12 w-12'}`}>
              <User className={big ? 'h-10 w-10' : 'h-6 w-6'} />
            </div>
            <p className={big ? 'text-base' : 'text-xs'}>{name || 'Participant'}</p>
            {!camOn && !screenOn && <p className="text-[10px] text-slate-500">camera off</p>}
          </div>
        </div>
      )}

      {/* Spotlight badge */}
      {big && (
        <div className="absolute top-2 left-2 flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold text-amber-100 bg-amber-600/80 backdrop-blur px-2 py-0.5 rounded">
          {isPresenter ? 'PRESENTING' : 'HOST'}
        </div>
      )}
      {/* Host pill for non-spotlight host */}
      {!big && isHost && (
        <div className="absolute top-1.5 left-1.5 text-[10px] uppercase tracking-wider font-semibold text-indigo-100 bg-indigo-600/80 backdrop-blur px-1.5 py-0.5 rounded">
          Host
        </div>
      )}

      <div className="absolute bottom-1.5 left-1.5 right-1.5 flex items-center gap-1.5 text-[11px] text-white">
        <span className="bg-black/60 backdrop-blur rounded px-1.5 py-0.5 truncate">
          {isLocal ? 'You' : name}{role === 'teacher' ? ' · Teacher' : role === 'admin' ? ' · Admin' : ''}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {screenOn && <Monitor className="h-3.5 w-3.5 text-emerald-400" />}
          {micOn ? <Mic className="h-3.5 w-3.5 text-emerald-400" /> : <MicOff className="h-3.5 w-3.5 text-rose-400" />}
          {camOn ? <VideoIcon className="h-3.5 w-3.5 text-emerald-400" /> : <VideoOff className="h-3.5 w-3.5 text-slate-400" />}
        </div>
      </div>
    </div>
  );
}

export default function LiveVideoGrid({
  myName,
  myRole,
  localStream,
  screenStream,
  mediaState,
  peers,
}) {
  // Spotlight rules (highest priority first):
  //   1. Whoever is currently sharing their screen → big tile
  //   2. The host (teacher/admin) — always bigger than student tiles
  //   3. If both screen + host exist, screen wins; host gets normal size.
  const screenSharer = peers.find((p) => p.screen);
  const meIsScreen = mediaState.screen;
  const meIsHost = myRole === 'teacher' || myRole === 'admin';
  const remoteHost = peers.find((p) => p.role === 'teacher' || p.role === 'admin');

  const spotlightId =
    meIsScreen ? 'local'
    : screenSharer ? screenSharer.peer_id
    : meIsHost ? 'local'
    : remoteHost ? remoteHost.peer_id
    : null;

  const tiles = [];

  // Local first
  tiles.push(
    <VideoTile
      key="local"
      isLocal
      stream={screenStream || localStream}
      name={myName || 'You'}
      role={myRole}
      camOn={mediaState.camera}
      micOn={mediaState.mic}
      screenOn={mediaState.screen}
      big={spotlightId === 'local'}
      isHost={meIsHost}
      isPresenter={meIsScreen}
    />
  );

  for (const p of peers) {
    tiles.push(
      <VideoTile
        key={p.peer_id}
        stream={p.stream}
        name={p.name}
        role={p.role}
        camOn={p.camera}
        micOn={p.mic}
        screenOn={p.screen}
        big={spotlightId === p.peer_id}
        isHost={p.role === 'teacher' || p.role === 'admin'}
        isPresenter={p.screen}
      />
    );
  }

  // When somebody is "spotlighted", we use a 2-row CSS grid so the big tile
  // visually dominates. With ≤2 tiles there's nothing to spotlight against,
  // so the grid is plain.
  const total = tiles.length;
  const hasBig = !!spotlightId && total > 1;
  const cols = hasBig ? 4 : (total <= 1 ? 1 : total <= 4 ? 2 : total <= 9 ? 3 : 4);
  const rows = hasBig ? 2 : 'auto';

  return (
    <div
      data-testid="live-video-grid"
      className="grid gap-2 h-full"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateRows: hasBig ? `repeat(${rows}, minmax(0, 1fr))` : undefined,
        gridAutoRows: hasBig ? undefined : '1fr',
      }}
    >
      {tiles}
    </div>
  );
}
